<template>
  <div class="musubi-page musubi-page--targeting">
    <header class="musubi-page__head">
      <h1>Targeting</h1>
      <p class="tg-lede">
        Who you're looking for. This powers your <strong>day-zero Companies House alerts</strong> —
        we raise a signal the moment a new or changed company matches the <strong>sectors</strong> and
        <strong>regions</strong> you pick here — and it feeds your pipeline audience filters. No targeting
        means no day-zero noise, on purpose.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <div v-if="message" class="alert alert-success py-2 small">{{ message }}</div>

    <div v-if="loading" class="tg-loading">Loading your targeting…</div>

    <template v-else>
      <div v-if="readOnly" class="alert alert-info py-2 small">
        <CygnusIcon name="lock" class="me-1" />This is the demo workspace — its targeting is read-only.
      </div>

      <!-- Empty state — mirrors the Companies House "tell us who you're looking for" nudge. -->
      <div v-if="!hasAny && !dirty" class="tg-empty">
        <div class="tg-empty__icon"><CygnusIcon name="pin" /></div>
        <div class="tg-empty__title">Tell us who you're looking for</div>
        <div class="tg-empty__hint">
          You haven't set any targeting yet, so the Companies House feed is empty by design. Pick the
          <strong>SIC sectors</strong> you sell into and (optionally) a <strong>region</strong> to keep it
          local — we'll match new incorporations against them the day they hit the register.
        </div>
      </div>

      <!-- ── Sectors ─────────────────────────────────────────────────────── -->
      <MusubiSectionCard title="Sectors you target" icon="building" subtitle="The SIC industries you sell into">
        <p class="tg-help">
          Pick specific industries (e.g. accountancy, legal, property) or a whole SIC section. We match a
          new company's registered SIC codes against these — the more specific, the less noise.
        </p>
        <MusubiSectorSelect v-model="sectorTokens" placeholder="Add an industry or SIC section…" :class="{ 'tg-disabled': readOnly }" />
        <p v-if="!sectorTokens.length" class="tg-none"><CygnusIcon name="info-circle" class="me-1" />No sectors yet — you'll match companies in any industry (region still applies).</p>
      </MusubiSectionCard>

      <!-- ── Regions ─────────────────────────────────────────────────────── -->
      <MusubiSectionCard title="Regions you target" icon="pin" subtitle="Keep it local (optional)">
        <p class="tg-help">
          Add one or more UK postcode areas to keep alerts local — e.g. Oxford (OX), or leave this empty to
          watch the whole UK. Region matches a company's registered postcode area.
        </p>

        <div class="tg-region">
          <div class="tg-region__chips" v-if="regionCodes.length">
            <span v-for="code in regionCodes" :key="code" class="tg-chip">
              {{ regionLabel(code) }}
              <button type="button" class="tg-chip__x" title="Remove" :disabled="readOnly" @click="removeRegion(code)">&times;</button>
            </span>
          </div>
          <div class="tg-region__picker" ref="regionPicker">
            <input
              v-model="regionQuery"
              type="text"
              class="form-control form-control-sm tg-region__search"
              placeholder="Search a region or postcode area…"
              :disabled="readOnly"
              @focus="regionOpen = true"
            />
            <div v-if="regionOpen && !readOnly" class="tg-region__panel">
              <div v-if="!visibleRegions.length" class="tg-region__empty">No match.</div>
              <button
                v-for="r in visibleRegions"
                :key="r.code"
                type="button"
                class="tg-region__opt"
                :class="{ 'is-selected': regionCodes.includes(r.code) }"
                @click="toggleRegion(r.code)"
              >
                <span class="tg-region__code">{{ r.code }}</span>
                <span class="tg-region__lbl">{{ r.label }}</span>
                <CygnusIcon name="check" class="tg-region__tick" v-if="regionCodes.includes(r.code)" />
              </button>
              <div v-if="regionTruncated" class="tg-region__more">Keep typing to narrow the list…</div>
            </div>
          </div>
        </div>
        <p v-if="!regionCodes.length" class="tg-none"><CygnusIcon name="info-circle" class="me-1" />No region — you'll match matching sectors anywhere in the UK.</p>
      </MusubiSectionCard>

      <!-- ── Save bar ────────────────────────────────────────────────────── -->
      <div class="tg-savebar">
        <p class="tg-timing">
          <CygnusIcon name="clock" class="me-1" />
          Changes take effect on the <strong>next matching run</strong> — Companies House alerts refresh
          within about 5–15 minutes; pipeline audiences pick this up on their next sync.
        </p>
        <div class="tg-savebar__actions">
          <button v-if="dirty" type="button" class="btn btn-link" :disabled="saving" @click="reset">Discard changes</button>
          <button type="button" class="btn btn-primary" :disabled="saving || readOnly || !dirty" @click="save">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="saving" />
            <CygnusIcon name="check-circle" class="me-1" v-else />Save targeting
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * AdminTargeting — the tenant-facing "who you're looking for" editor over the
 * tenant's own fit_criteria (sectors + regions), gated Ops::ACQUIRE. Reuses the
 * canonical MusubiSectorSelect (SIC section/code picker, shared with the prospects
 * filter + pipeline wizard) for sectors, and an inline postcode-area multi-select
 * for regions. Saves are batched: POST /admin/targeting reconciles the tenant's
 * managed targeting rows server-side. NOT plan-gated — targeting is core.
 */
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiSectionCard from "@root/components/ui/MusubiSectionCard.vue";
import MusubiSectorSelect from "@root/components/MusubiSectorSelect.vue";

const MAX_REGION_ROWS = 40;

export default {
  name: 'AdminTargeting',
  components: { MusubiSectionCard, MusubiSectorSelect },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loading: true,
      saving: false,
      error: null,
      message: '',
      readOnly: false,

      // editable state
      sectorTokens: [],   // list of SIC codes (and/or section letters newly picked)
      regionCodes: [],    // list of postcode-area codes

      // pristine snapshot (for dirty detection + discard)
      pristine: { sectors: [], regions: [] },

      // region picker vocabulary + UI
      regionVocab: [],    // [{code,label}]
      regionQuery: '',
      regionOpen: false,
    };
  },
  computed: {
    hasAny() {
      return this.sectorTokens.length > 0 || this.regionCodes.length > 0;
    },
    dirty() {
      return !this.sameSet(this.sectorTokens, this.pristine.sectors)
        || !this.sameSet(this.regionCodes, this.pristine.regions);
    },
    filteredRegions() {
      const q = this.regionQuery.trim().toLowerCase();
      if (!q) return this.regionVocab;
      return this.regionVocab.filter((r) =>
        r.code.toLowerCase().includes(q) || r.label.toLowerCase().includes(q));
    },
    visibleRegions() {
      return this.filteredRegions.slice(0, MAX_REGION_ROWS);
    },
    regionTruncated() {
      return this.filteredRegions.length > MAX_REGION_ROWS;
    },
  },
  mounted() {
    this.load();
    document.addEventListener('click', this.onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    async load() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/targeting');
        const p = res?.data ?? res;
        const sectors = Array.isArray(p?.sectors) ? p.sectors.map((s) => String(s.code)) : [];
        const regions = Array.isArray(p?.regions) ? p.regions.map((r) => String(r.code)) : [];
        this.sectorTokens = sectors.slice();
        this.regionCodes = regions.slice();
        this.pristine = { sectors: sectors.slice(), regions: regions.slice() };
        this.regionVocab = Array.isArray(p?.region_vocabulary) ? p.region_vocabulary : [];
        this.readOnly = !!p?.read_only;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not load your targeting.';
      }
      this.loading = false;
    },
    async save() {
      if (this.readOnly || !this.dirty) return;
      this.saving = true; this.error = null; this.message = '';
      try {
        const res = await this.api.post('/api/v1/admin/targeting', {
          sectors: this.sectorTokens.slice(),
          regions: this.regionCodes.slice(),
        });
        const p = res?.data ?? res;
        const sectors = Array.isArray(p?.sectors) ? p.sectors.map((s) => String(s.code)) : [];
        const regions = Array.isArray(p?.regions) ? p.regions.map((r) => String(r.code)) : [];
        this.sectorTokens = sectors.slice();
        this.regionCodes = regions.slice();
        this.pristine = { sectors: sectors.slice(), regions: regions.slice() };
        this.message = 'Targeting saved. New matches appear on the next matching run.';
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not save your targeting.';
      }
      this.saving = false;
    },
    reset() {
      this.sectorTokens = this.pristine.sectors.slice();
      this.regionCodes = this.pristine.regions.slice();
      this.message = '';
      this.error = null;
    },
    // ── region picker ──
    regionLabel(code) {
      const r = this.regionVocab.find((x) => x.code === code);
      return r ? r.label : code;
    },
    toggleRegion(code) {
      const i = this.regionCodes.indexOf(code);
      if (i === -1) this.regionCodes.push(code); else this.regionCodes.splice(i, 1);
    },
    removeRegion(code) {
      this.regionCodes = this.regionCodes.filter((c) => c !== code);
    },
    onDocClick(ev) {
      const el = this.$refs.regionPicker;
      if (el && !el.contains(ev.target)) this.regionOpen = false;
    },
    // ── helpers ──
    sameSet(a, b) {
      if (a.length !== b.length) return false;
      const sb = new Set(b);
      return a.every((x) => sb.has(x));
    },
  },
};
</script>

<style scoped>
.musubi-page--targeting { padding: var(--m-space-6); max-width: 880px; display: flex; flex-direction: column; gap: var(--m-space-4); }
.musubi-page__head h1 { font-weight: 750; margin-bottom: 4px; }
.tg-lede { color: var(--m-text-secondary); font-size: 0.92rem; line-height: 1.55; margin: 0; max-width: 70ch; }
.tg-loading { color: var(--m-text-tertiary); padding: var(--m-space-4); }

.tg-help { color: var(--m-text-secondary); font-size: 0.84rem; margin: 0 0 10px; line-height: 1.5; }
.tg-none { color: var(--m-text-tertiary); font-size: 0.78rem; margin: 8px 0 0; }
.tg-none i { color: var(--m-accent); }

/* Empty state — echoes the CH feed nudge */
.tg-empty {
  text-align: center; padding: 22px 18px; border: 1px dashed var(--m-border-strong);
  border-radius: var(--m-radius-lg); background: var(--m-bg-content);
}
.tg-empty__icon { font-size: 1.7rem; color: var(--m-accent); margin-bottom: 8px; }
.tg-empty__title { font-size: 1.05rem; font-weight: 700; color: var(--m-text-primary); margin-bottom: 6px; }
.tg-empty__hint { color: var(--m-text-secondary); font-size: 0.86rem; line-height: 1.55; max-width: 60ch; margin: 0 auto; }

.tg-disabled { pointer-events: none; opacity: 0.6; }

/* Region multi-select */
.tg-region { display: flex; flex-direction: column; gap: 8px; }
.tg-region__chips { display: flex; flex-wrap: wrap; gap: 5px; }
.tg-chip {
  display: inline-flex; align-items: center; gap: 5px; padding: 2px 5px 2px 9px; border-radius: 100px;
  background: var(--m-accent-soft); color: var(--m-accent); font-size: 0.74rem; font-weight: 500;
}
.tg-chip__x { border: 0; background: transparent; color: inherit; cursor: pointer; font-size: 1rem; line-height: 1; padding: 0 2px; }
.tg-chip__x:hover:not(:disabled) { color: var(--m-status-danger); }
.tg-chip__x:disabled { opacity: 0.5; cursor: not-allowed; }

.tg-region__picker { position: relative; max-width: 420px; }
.tg-region__panel {
  position: absolute; z-index: 40; top: calc(100% + 3px); left: 0; width: 100%;
  background: var(--m-bg-card); border: 1px solid var(--m-border-strong); border-radius: var(--m-radius);
  box-shadow: var(--m-shadow-panel); padding: 6px; max-height: 300px; overflow-y: auto;
}
.tg-region__opt {
  display: flex; align-items: center; gap: 8px; width: 100%; text-align: left; border: 0; background: transparent;
  cursor: pointer; padding: 6px 7px; border-radius: var(--m-radius); font-size: 0.82rem; color: var(--m-text-primary);
}
.tg-region__opt:hover { background: var(--m-bg-subtle); }
.tg-region__opt.is-selected { background: var(--m-accent-soft); }
.tg-region__code { flex: 0 0 auto; min-width: 34px; font-weight: 600; color: var(--m-accent); font-variant-numeric: tabular-nums; }
.tg-region__lbl { flex: 1 1 auto; color: var(--m-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tg-region__tick { color: var(--m-accent); }
.tg-region__empty, .tg-region__more { padding: 8px 7px; font-size: 0.76rem; color: var(--m-text-tertiary); font-style: italic; }

/* Save bar */
.tg-savebar {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  padding: 14px 16px; background: var(--m-bg-card); border: 1px solid var(--m-border);
  border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card);
}
.tg-timing { margin: 0; font-size: 0.8rem; color: var(--m-text-secondary); max-width: 56ch; line-height: 1.5; }
.tg-timing i { color: var(--m-accent); }
.tg-savebar__actions { display: flex; align-items: center; gap: 8px; margin-left: auto; }

@media (max-width: 640px) {
  .tg-savebar { flex-direction: column; align-items: stretch; }
  .tg-savebar__actions { justify-content: flex-end; }
}
</style>
